import { useState } from 'react'
import { Badge } from '@/components/Badge'
import { Button } from '@/components/Button'
import { Card, CardBody, CardHeader } from '@/components/Card'
import { EmptyState } from '@/components/EmptyState'
import { ErrorNotice } from '@/components/ErrorNotice'
import { SkeletonRows } from '@/components/Skeleton'
import { ACTIVITY_PAGE_SIZE, useAdminActivity } from '@/hooks/useAdmin'
import { useNow } from '@/hooks/useNow'
import { formatDateTime, relativeTime } from '@/lib/format'

const TH = 'signage px-4 py-2.5 text-[9px] font-semibold text-faint'
const TD = 'px-4 py-3 align-top'

export function AdminActivityPage() {
  const [offset, setOffset] = useState(0)
  const activity = useAdminActivity(offset)
  const now = useNow(30_000)

  const items = activity.data?.items ?? []
  const total = activity.data?.total ?? 0
  const hasMore = offset + items.length < total

  return (
    <Card>
      <CardHeader
        title="Activity"
        description="Everything Hermes did, newest first. Kept for troubleshooting."
      />
      <CardBody flush>
        {activity.isPending ? (
          <div className="p-4">
            <SkeletonRows rows={8} />
          </div>
        ) : activity.isError ? (
          <div className="p-4">
            <ErrorNotice error={activity.error} onRetry={() => void activity.refetch()} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              title="Nothing logged yet"
              description="Actions show up here as they happen."
            />
          </div>
        ) : (
          <>
            {/* Phones: the message leads, the metadata sits under it as
                label/value pairs — never a sideways-scrolling log table. */}
            <div className="sm:hidden" aria-busy={activity.isFetching}>
              {items.map((entry) => (
                <div key={entry.id} className="board-row px-4 py-3.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="signage-tight min-w-0 text-sm text-ink">{entry.message}</p>
                    <time
                      dateTime={entry.createdAt}
                      title={formatDateTime(entry.createdAt)}
                      className="shrink-0 font-mono text-[11px] text-faint"
                    >
                      {relativeTime(entry.createdAt, now)}
                    </time>
                  </div>

                  <dl className="mt-3 grid grid-cols-3 gap-x-3 gap-y-2.5">
                    <div className="min-w-0">
                      <dt className="signage text-[9px] text-faint">Type</dt>
                      <dd className="mt-0.5 truncate font-mono text-xs text-ink">{entry.type}</dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="signage text-[9px] text-faint">By</dt>
                      <dd className="mt-0.5 truncate font-mono text-xs text-ink">
                        {entry.actorUsername ?? '—'}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="signage text-[9px] text-faint">About</dt>
                      <dd className="mt-0.5 truncate font-mono text-xs text-ink">
                        {entry.subjectUsername ?? '—'}
                      </dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>

            <div className="hidden sm:block">
              <table className="w-full text-left" aria-busy={activity.isFetching}>
                <thead>
                  <tr className="border-b border-line">
                    <th scope="col" className={TH}>
                      When
                    </th>
                    <th scope="col" className={TH}>
                      Type
                    </th>
                    <th scope="col" className={TH}>
                      What happened
                    </th>
                    <th scope="col" className={TH}>
                      Who
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((entry) => (
                    <tr key={entry.id} className="border-b border-line last:border-b-0">
                      <td className={`${TD} whitespace-nowrap`}>
                        <time
                          dateTime={entry.createdAt}
                          title={formatDateTime(entry.createdAt)}
                          className="font-mono text-xs text-muted"
                        >
                          {relativeTime(entry.createdAt, now)}
                        </time>
                      </td>
                      <td className={TD}>
                        <Badge>{entry.type}</Badge>
                      </td>
                      <td className={`${TD} text-sm text-ink`}>{entry.message}</td>
                      <td className={`${TD} font-mono text-xs text-muted`}>
                        {entry.actorUsername ?? '—'}
                        {entry.subjectUsername ? (
                          <span className="block text-[11px] text-faint">
                            → {entry.subjectUsername}
                          </span>
                        ) : null}
                        {entry.ip ? (
                          <span className="block text-[11px] text-faint">{entry.ip}</span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
              <p className="flex items-baseline gap-1.5">
                <span className="font-mono text-[11px] text-faint">
                  {offset + 1}–{offset + items.length}/{total}
                </span>
                <span className="signage text-[9px] text-faint">entries</span>
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={offset === 0 || activity.isFetching}
                  onClick={() => setOffset(Math.max(0, offset - ACTIVITY_PAGE_SIZE))}
                >
                  Newer
                </Button>
                <Button
                  size="sm"
                  disabled={!hasMore || activity.isFetching}
                  onClick={() => setOffset(offset + ACTIVITY_PAGE_SIZE)}
                >
                  Older
                </Button>
              </div>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  )
}
